import "./profile_checklist.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import { Body } from "@sprout/typography.tsx";
import useTranslation from "@hooks/use_translation.ts";
import useProfile from "@/queries/use_profile.ts";
import LoadingSpinner from "@sprout/loading_spinner.tsx";
import { IconButton } from "@sprout/icon_button.tsx";
import ArrowUpIcon from "@sprout/icons/arrow_up.tsx";
import ArrowDownIcon from "@sprout/icons/arrow_down.tsx";
import { buildChecklistItems } from "@components/profile/profile_checklist_items.ts";
import ProfileChecklistRow from "@components/dashboard/profile/profile_checklist_row.tsx";
import ProfileSectionModal from "@components/profile/modals/section_modal.tsx";
import { usePage } from "@inertiajs/react";
import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";

const ProfileChecklist = () => {
  const { t } = useTranslation("user", { keyPrefix: "profile_checklist" });
  const { data, isLoading } = useProfile();
  const [expanded, setExpanded] = useState(true);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const { workPreferencesExperimentEnabled } = usePage<{
    workPreferencesExperimentEnabled?: boolean;
  }>().props;
  const items = useMemo(() => {
    const built = data ? buildChecklistItems(data) : [];
    if (!workPreferencesExperimentEnabled) {
      return built;
    }
    const workPreferences = built.find((item) => item.key === "work_preferences");
    if (!workPreferences) {
      return built;
    }
    const reordered = built.filter((item) => item !== workPreferences);
    const educationIndex = reordered.findIndex((item) => item.key === "education");
    reordered.splice(educationIndex + 1, 0, workPreferences);
    return reordered;
  }, [data, workPreferencesExperimentEnabled]);

  const { trackEvent } = useSnowplowTracker();
  const shownRef = useRef(false);

  useEffect(() => {
    if (data && !shownRef.current) {
      shownRef.current = true;
      trackEvent(SnowplowEvent.ProfileWidgetShown, {
        variant: workPreferencesExperimentEnabled ? "profile_b" : "profile_a",
      });
    }
  }, [data, trackEvent, workPreferencesExperimentEnabled]);

  if (isLoading || !data) {
    return (
      <div className="profile_checklist">
        <div className="flex justify-center">
          <LoadingSpinner size="small" />
        </div>
      </div>
    );
  }

  const completeCount = items.filter((item) => item.status === "complete").length;

  return (
    <div className="profile_checklist">
      <div className="profile_checklist__header">
        <div className="profile_checklist__header-title">
          <Body metadata medium>
            {t("title")}
          </Body>
          <Body metadata medium>
            •
          </Body>
          <Body metadata secondary>
            {t("completed", { count: completeCount, total: items.length })}
          </Body>
        </div>
        <IconButton
          icon={expanded ? ArrowUpIcon : ArrowDownIcon}
          size="md"
          label={t(expanded ? "collapse" : "expand")}
          onClick={() => setExpanded((value) => !value)}
        />
      </div>
      {expanded && (
        <ul className="profile_checklist__rows">
          {items.map((item) => (
            <ProfileChecklistRow
              key={item.key}
              item={item}
              t={t}
              onEdit={() => setOpenKey(item.key)}
            />
          ))}
        </ul>
      )}
      <ProfileSectionModal
        openKey={openKey}
        onOpenChange={(isOpen) => !isOpen && setOpenKey(null)}
        data={data}
        source="dashboard"
      />
    </div>
  );
};

export default ProfileChecklist;
